import { useEffect, useState } from 'react';
import { useAuthStore, getAuthSessionEpoch } from '@/stores/authStore';
import { createUsBrowserMarketClient } from '@/lib/polymarketUs/browserMarketClient';
import { UsClientError } from '@/lib/polymarketUs/tradingClient';
import { usGameKeyForBoardKey } from '@/lib/polymarketUs/gameMapping';
import { usGameSportForKey } from '@/lib/polymarketUs/sports';
import { usGamePropTarget } from '@/lib/polymarketUs/gamePropTarget';
import { usPlayerTarget } from '@/lib/polymarketUs/playerTarget';
import type { UsDiscoveryMarket, UsMarketClient } from '@/lib/polymarketUs/marketClient';

const MAX_PAGES = 20;
const EMPTY: readonly UsDiscoveryMarket[] = [];
export type UsPlayerCatalogue = { status: 'loading' | 'ready' | 'unavailable'; markets: readonly UsDiscoveryMarket[] };

/** Complete, one-game, account-free list. Never present a truncated page as
 * complete, combine catalogue generations, or pair names across venues. */
export async function readUsPlayerCatalogue(client: Pick<UsMarketClient, 'listPublicMarkets'>, gameKey: string, includeGameProps = false): Promise<UsDiscoveryMarket[]> {
  const sport = usGameSportForKey(gameKey);
  if (sport !== 'NFL' && sport !== 'NBA' && !(includeGameProps && sport === 'CFB')) throw new UsClientError('US_CLIENT_INVALID_REQUEST');
  const rows: UsDiscoveryMarket[] = [];
  let generation: string | null = null, after: string | undefined;
  const seen = new Set<string>();
  for (let page = 0; page < MAX_PAGES; page++) {
    const next = await client.listPublicMarkets({ q: gameKey, sport, marketType: 'props', ...(after ? { after } : {}) });
    if (generation !== null && generation !== next.generation) throw new UsClientError('US_CATALOGUE_CHANGED');
    generation = next.generation;
    for (const row of next.markets) {
      if (row.gameKey !== gameKey || !(usPlayerTarget(row) || includeGameProps && usGamePropTarget(row))) continue;
      if (seen.has(row.marketSlug)) throw new UsClientError('US_CLIENT_INVALID_RESPONSE');
      seen.add(row.marketSlug); rows.push(row);
    }
    if (!next.nextCursor) return rows;
    if (next.nextCursor === after) throw new UsClientError('US_CLIENT_INVALID_RESPONSE');
    after = next.nextCursor;
  }
  throw new UsClientError('US_CATALOGUE_UNAVAILABLE');
}

export function useUsPlayerCatalogue(boardKey: string, live: boolean, includeGameProps = false): UsPlayerCatalogue {
  const authenticated = useAuthStore(s => s.status === 'authenticated');
  const user = useAuthStore(s => s.user);
  const epoch = getAuthSessionEpoch();
  const gameKey = usGameKeyForBoardKey(boardKey);
  const key = authenticated && user?.id && (gameKey?.startsWith('nfl|') || gameKey?.startsWith('nba|') || includeGameProps && gameKey?.startsWith('cfb|')) ? `${epoch}|${user.id}|${gameKey}|${includeGameProps}` : null;
  const [client] = useState(createUsBrowserMarketClient);
  const [state, setState] = useState<{ key: string; value: UsPlayerCatalogue } | null>(null);
  useEffect(() => {
    if (!key || !gameKey) return;
    let cancelled = false, pending = false;
    const load = async () => {
      if (cancelled || pending) return;
      pending = true;
      try {
        const markets = await readUsPlayerCatalogue(client, gameKey, includeGameProps);
        if (!cancelled) setState({ key, value: { status: 'ready', markets } });
      } catch {
        // Retain only the filter inventory for this same identity on failure.
        // The section still withholds rows/quotes while status is unavailable.
        // Do not collapse the selected stat/player options on a transient read.
        if (!cancelled) setState(prev => ({ key, value: { status: 'unavailable', markets: prev?.key === key ? prev.value.markets : EMPTY } }));
      } finally { pending = false; }
    };
    void load();
    // First inventory read enables the existing tab for US-only games.
    // Recurring work exists only while Player Props is actually visible.
    const timer = live ? setInterval(() => void load(), 30_000) : null;
    return () => { cancelled = true; if (timer !== null) clearInterval(timer); client.invalidate(); };
  }, [key, gameKey, client, live, includeGameProps]);
  return state?.key === key ? state.value : { status: key ? 'loading' : 'unavailable', markets: EMPTY };
}
